import { useMemo } from "react";
import { useMenu, usePermissions } from "@refinedev/core";
import type { TreeMenuItem } from "@refinedev/core";
import { useNavigate } from "react-router";
import { Drawer, Grid, Tooltip, Typography } from "antd";
import { useEdel } from "../../contexts/color-mode";
import { EdelLogomark } from "../brand-logo";
import { AREA_WORKSPACE, canSeeArea } from "../../constants";
import { useNav } from "./nav-context";

const { Text } = Typography;

const RAIL_W = 76;
const PANEL_W = 214;

const containsKey = (item: TreeMenuItem, key: string): boolean =>
  item.key === key || (item.children ?? []).some((c) => containsKey(c, key));

/**
 * İki katmanlı navigasyon:
 *  - Sol dar ray: ana alanlar (ikon + tooltip).
 *  - Yanındaki panel: aktif alanın alt sayfaları.
 * Aktif alan mevcut route'tan (useMenu.selectedKey) türetilir.
 */
export const AppSider = () => {
  const { menuItems: allMenuItems, selectedKey } = useMenu();
  const { data: perms } = usePermissions<{ scopes?: string[] } | null>({});
  const scopeList = perms?.scopes ?? null;
  // RBAC (FAZ D): kullanıcının scope'una göre üst-alanları süz (boş scope = tam yetki).
  const menuItems = useMemo(
    () => allMenuItems.filter((it) => canSeeArea(it.key as string, scopeList)),
    [allMenuItems, scopeList]
  );
  const navigate = useNavigate();
  const { palette: p } = useEdel();
  const { mobileOpen, setMobileOpen } = useNav();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.lg === false;

  const activeTop = useMemo(
    () => menuItems.find((it) => containsKey(it, selectedKey)) ?? menuItems[0],
    [menuItems, selectedKey]
  );

  // Ray yalnızca aktif çalışma alanının (CRM / Planlama / Drive) üst
  // alanlarını gösterir. Alanlar arası geçiş Header'daki app-switcher ile.
  const currentWorkspace =
    AREA_WORKSPACE[(activeTop?.key as string) ?? ""] ?? "crm";
  const railItems = useMemo(
    () =>
      menuItems.filter(
        (it) => (AREA_WORKSPACE[it.key as string] ?? "crm") === currentWorkspace
      ),
    [menuItems, currentWorkspace]
  );

  const panelItems: TreeMenuItem[] = activeTop
    ? activeTop.children && activeTop.children.length > 0
      ? activeTop.children
      : [activeTop]
    : [];

  const go = (route?: string) => {
    if (!route) return;
    navigate(route);
    setMobileOpen(false);
  };

  const onAreaClick = (item: TreeMenuItem) => {
    if (item.children && item.children.length > 0) {
      const first = item.children.find((c) => c.route);
      go(first?.route ?? item.route);
    } else {
      go(item.route);
    }
  };

  const rail = (
    <div
      style={{
        width: RAIL_W,
        flex: "0 0 auto",
        background: p.surface,
        borderRight: `1px solid ${p.borderLight}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: 14,
        paddingBottom: 14,
        gap: 6,
        height: "100%",
        overflowY: "auto",
      }}
    >
      <Tooltip title="Başlangıç ekranı" placement="right">
        <button
          onClick={() => go("/hub")}
          aria-label="Başlangıç ekranı"
          style={{
            marginBottom: 12,
            color: p.primary,
            display: "inline-flex",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 0,
          }}
        >
          <EdelLogomark size={30} />
        </button>
      </Tooltip>
      {railItems.map((item) => {
        const active = item.key === activeTop?.key;
        return (
          <Tooltip key={item.key} title={item.label} placement="right">
            <button
              onClick={() => onAreaClick(item)}
              aria-label={typeof item.label === "string" ? item.label : undefined}
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
                background: active ? p.primarySoft : "transparent",
                color: active ? p.primary : p.textSoft,
                transition: "background .15s, color .15s",
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = p.surface2;
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = "transparent";
              }}
            >
              {item.icon}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );

  const panel = (
    <div
      style={{
        width: PANEL_W,
        flex: "0 0 auto",
        background: p.surface,
        borderRight: `1px solid ${p.borderLight}`,
        height: "100%",
        overflowY: "auto",
        padding: "18px 12px",
      }}
    >
      <Text
        style={{
          display: "block",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: p.textSoft,
          padding: "0 10px",
          marginBottom: 14,
        }}
      >
        {activeTop?.label}
      </Text>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {panelItems.map((child) => {
          const active = child.key === selectedKey;
          return (
            <button
              key={child.key}
              onClick={() => go(child.route)}
              style={{
                textAlign: "left",
                border: "none",
                cursor: "pointer",
                borderRadius: 10,
                padding: "9px 12px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 14,
                fontWeight: active ? 600 : 500,
                background: active ? p.primarySoft : "transparent",
                color: active ? p.primary : p.text,
                transition: "background .15s, color .15s",
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = p.surface2;
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = "transparent";
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  color: active ? p.primary : p.textSoft,
                  display: "inline-flex",
                }}
              >
                {child.icon}
              </span>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {child.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const inner = (
    <div style={{ display: "flex", height: "100%" }}>
      {rail}
      {panel}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        width={RAIL_W + PANEL_W}
        closable={false}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ height: "100%" }}>{inner}</div>
      </Drawer>
    );
  }

  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        alignSelf: "flex-start",
        height: "100vh",
        flex: "0 0 auto",
        zIndex: 9,
      }}
    >
      {inner}
    </div>
  );
};
